import {
  CloseOutlined,
  ExpandAltOutlined,
  FormOutlined
} from "@ant-design/icons"
import { Button, Flex, Tooltip, Typography } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"
import { useLocation, useNavigate } from "react-router-dom"

import { WIZARD_STEPS } from "../helpers/wizard-steps.ts"
import {
  clearMinimizedWizard,
  useMinimizedWizard
} from "../hooks/use-minimized-wizard.ts"

const { Text } = Typography

/**
 * A minimized signature request, docked at the bottom of every page until it
 * is restored or dismissed. Dismissing it deletes nothing: a saved draft stays
 * in EZ-Sign's Drafts, which is what the tooltip says.
 */
export function MinimizedWizardDock() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const wizard = useMinimizedWizard()

  // Already open: the bar would only point at the window in front of it.
  if (!wizard || location.pathname === wizard.url.split("?")[0]) return null

  const restore = () => {
    clearMinimizedWizard()
    navigate(wizard.url, {
      state: { returnTo: `${location.pathname}${location.search}` }
    })
  }

  return (
    <section
      aria-label={t("esign.dock.label")}
      className="fixed bottom-4 right-24 z-[1000] w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-solid border-border bg-surface px-3 py-2 shadow-card max-md:right-4"
    >
      <Flex align="center" gap="small">
        <FormOutlined aria-hidden className="text-primary" />
        <button
          type="button"
          onClick={restore}
          className="flex min-w-0 flex-1 cursor-pointer flex-col items-start border-0 bg-transparent p-0 text-left"
        >
          <Text strong ellipsis className="max-w-full">
            {wizard.title}
          </Text>
          <Text type="secondary" className="text-xs">
            {wizard.envelopeId
              ? t("esign.dock.savedAt", {
                  number: wizard.stepNumber,
                  count: WIZARD_STEPS.length
                })
              : t("esign.dock.notSaved")}
          </Text>
        </button>
        <Tooltip title={t("esign.dock.restore")}>
          <Button
            type="text"
            size="small"
            icon={<ExpandAltOutlined />}
            aria-label={t("esign.dock.restore")}
            onClick={restore}
          />
        </Tooltip>
        <Tooltip
          title={t(
            wizard.envelopeId ? "esign.dock.dismissDraft" : "esign.dock.dismiss"
          )}
        >
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            aria-label={t(
              wizard.envelopeId
                ? "esign.dock.dismissDraft"
                : "esign.dock.dismiss"
            )}
            onClick={clearMinimizedWizard}
          />
        </Tooltip>
      </Flex>
    </section>
  )
}
